/**
 * QDesktop topic bus。對應 docs/protocol.md。
 *
 * 硬規則：
 *   - 零依賴
 *   - 不引用任何 DOM API
 *   - 對「訊息怎麼傳」一無所知（傳輸層之後接上去）
 */

import { DEFAULT_RECT, INSTANCE_KERNEL_NODES, SYS_NODES } from './kernel-nodes.ts'
import {
  APP_ROOT,
  SYS_ROOT,
  DEFAULT_CALL_TIMEOUT_MS,
  DEFAULT_GATE_TIMEOUT_MS,
  MAX_GATE_TIMEOUT_MS,
  DEFAULT_WRITE_TIMEOUT_MS,
  MAX_CALL_TIMEOUT_MS,
  MAX_WRITE_TIMEOUT_MS,
  PROTOCOL_VERSION,
  busError,
  encodeValue,
  fault,
  hasReservedSegment,
  matchSegments,
  splitPath,
  systemClock,
  validatePath,
  validatePattern,
  validateValue,
} from '@qdesktop/protocol'
import type { Clock } from '@qdesktop/protocol'
import type {
  Access,
  Activity,
  BusError,
  CallHandler,
  CallResult,
  CatalogEntry,
  GateLevel,
  InitialWindow,
  InstanceMeta,
  InstanceSummary,
  LayoutCell,
  LayoutTemplate,
  Lifecycle,
  PendingApproval,
  Viewport,
  Message,
  MessageHandler,
  NodeDef,
  NodeKind,
  RegisterOptions,
  Registration,
  SessionInstance,
  SessionSnapshot,
  StateSnapshot,
  Unsubscribe,
  WindowRect,
  WindowState,
  WriteHandler,
  WriteOutcome,
  WriteResult,
} from '@qdesktop/protocol'

// ─────────────────────────────────────────────── 內部結構

interface NodeRecord {
  /** 絕對路徑 */
  path: string
  /** 相對於實例前綴；$sys 節點等於絕對路徑 */
  relPath: string
  def: NodeDef
  instanceUid: string | null
  kernelOwned: boolean
  hasValue: boolean
  value: unknown
  updated_at: number
  seq: number
}

interface InstanceRecord {
  uid: string
  prefix: string
  /** 從哪個網址載入。只有宿主知道，透過 declareLoading 提供。 */
  url: string | null
  title: string
  summary: string
  groups: Record<string, string>
  protocolVersion: string
  lifecycle: Lifecycle
  appNodes: Set<string>
  kernelNodes: Set<string>
  writeHandler: WriteHandler | null
  callHandler: CallHandler | null
  pending: Set<(error: BusError) => void>
  /** true = app 已註冊且存活 */
  live: boolean
  /**
   * 存檔裡這個實例的設定，等 app 註冊時灌進節點。
   *
   * 為什麼在核心而不是宿主註冊完再寫回去：宿主不知道 app 什麼時候會 publish
   * 自己的出廠值，兩邊各寫一次的順序只能靠運氣。在這裡的話節點一出生就帶著
   * 還原的值，沒有那個「先看到預設、一下又跳掉」的空窗。
   */
  pendingSettings: Record<string, unknown> | null
}

interface Subscription {
  id: number
  pattern: string
  segments: string[]
  handler: MessageHandler
  active: boolean
}

export interface BusOptions {
  clock?: Clock
  /**
   * 訂閱者的 handler 拋出例外時呼叫。
   * 未提供時，例外會在獨立的 microtask 中重新拋出——刻意不吞掉錯誤。
   */
  onHandlerError?: (error: unknown, pattern: string) => void
}

/**
 * 由宿主提供的實際開啟實作。核心只負責查目錄與轉交，不知道 iframe 是什麼。
 */
/**
 * 開啟一個目錄項目。`placement` 是呼叫方指定的擺放方式，省略的欄位沿用
 * 目錄的 `default_window`。
 *
 * 之所以讓 launch 收擺放參數，而不是叫呼叫方開完再寫 `$window/rect`：後者會
 * 讓視窗先出現在預設位置再跳過去。人拖曳是連續的，agent 也該有同樣的能力。
 */
export type Launcher = (
  entry: CatalogEntry,
  placement?: InitialWindow,
) => Promise<{ uid: string }> | { uid: string }

// ─────────────────────────────────────────────── Bus

export class Bus {
  private readonly clock: Clock
  private readonly onHandlerError: ((error: unknown, pattern: string) => void) | null
  private readonly nodes = new Map<string, NodeRecord>()
  private readonly instances = new Map<string, InstanceRecord>()
  private readonly subs = new Map<number, Subscription>()
  private readonly gates = new Map<
    string,
    {
      entry: PendingApproval
      resolve: (outcome: { ok: false; error: BusError } | null) => void
      timer: ReturnType<typeof setTimeout>
    }
  >()
  private launcher: Launcher | null = null
  private catalog: CatalogEntry[] = []
  private layouts: LayoutTemplate[] = []
  private nextGateId = 1
  private nextSubId = 1
  private seqCounter = 0

  constructor(options: BusOptions = {}) {
    this.clock = options.clock ?? systemClock
    this.onHandlerError = options.onHandlerError ?? null
    this.installSysNodes()
  }

  // ───────────────────────────────── 讀取（§8.1）

  read(path: string): StateSnapshot | undefined {
    const node = this.nodes.get(path)
    if (!node || node.def.kind !== 'state' || !node.hasValue) return undefined
    return this.snapshot(node)
  }

  /**
   * 取得某個節點的定義。回傳的物件與 `$meta` 裡的那一份相同，
   * 因此 `def.path` 是**相對於實例前綴**的路徑。
   */
  describe(path: string): NodeDef | undefined {
    return this.nodes.get(path)?.def
  }

  readMany(pattern: string): StateSnapshot[] {
    const invalid = validatePattern(pattern)
    if (invalid) throw fault(invalid.code, invalid.message, { path: pattern })
    const segments = splitPath(pattern)
    const out: StateSnapshot[] = []
    for (const node of this.nodes.values()) {
      if (node.def.kind !== 'state' || !node.hasValue) continue
      if (!matchSegments(segments, splitPath(node.path))) continue
      out.push(this.snapshot(node))
    }
    return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
  }

  // ───────────────────────────────── 訂閱（§7）

  subscribe(pattern: string, handler: MessageHandler): Unsubscribe {
    const invalid = validatePattern(pattern)
    if (invalid) throw fault(invalid.code, invalid.message, { path: pattern })

    const sub: Subscription = {
      id: this.nextSubId++,
      pattern,
      segments: splitPath(pattern),
      handler,
      active: true,
    }
    this.subs.set(sub.id, sub)

    // §7.3 訂閱建立時補送所有匹配到的 state 保留值
    for (const node of this.nodes.values()) {
      if (node.def.kind !== 'state' || !node.hasValue) continue
      if (!matchSegments(sub.segments, splitPath(node.path))) continue
      this.dispatch(sub, {
        type: 'state',
        path: node.path,
        value: node.value,
        updated_at: node.updated_at,
        seq: node.seq,
        retained: true,
      })
    }

    return () => {
      sub.active = false
      this.subs.delete(sub.id)
    }
  }

  // ───────────────────────────────── 寫入（§8.2）

  async write(
    path: string,
    value: unknown,
    opts: { timeout?: number; origin?: string; gesture?: boolean } = {},
  ): Promise<WriteResult> {
    // 風險閘門在任何事情發生之前——被拒絕的操作不該留下任何痕跡。
    // 不需要攔截時**不能有 await**，否則實際動作會被延後一個 microtask，
    // 「進行中的操作」就變成「還沒開始的操作」，語意不同。
    const origin = opts.origin ?? null
    const hold = this.needsApproval(path, origin)
      ? await this.holdForApproval('write', path, value, origin)
      : null
    const result = hold ?? (await this.writeInternal(path, value, opts))
    const compact = compactActivityValue(value)
    this.emitActivity({
      kind: 'write',
      path,
      value: compact.value,
      ...(compact.truncated ? { truncated: true } : {}),
      origin: opts.origin ?? null,
      gesture: opts.gesture === true,
      ok: result.ok,
      ...(result.ok ? {} : { error: result.error }),
      at: this.clock.now(),
    })
    return result
  }

  private async writeInternal(
    path: string,
    value: unknown,
    opts: { timeout?: number; origin?: string } = {},
  ): Promise<WriteResult> {
    const invalidPath = validatePath(path)
    if (invalidPath) return { ok: false, error: invalidPath }

    const node = this.nodes.get(path)
    if (!node) {
      return {
        ok: false,
        error: busError('E_NOT_FOUND', `節點不存在：${path}`, {
          path,
          hint: '請先讀取 $sys/registry/instances 與該實例的 $meta 確認可用的節點路徑。',
        }),
      }
    }

    const notWritable = checkWritable(node.def.kind, node.def.access, path)
    if (notWritable) return { ok: false, error: notWritable }

    const encoded = encodeValue(value, path)
    if (!encoded.ok) return { ok: false, error: encoded.error }
    const incoming = encoded.clone

    // §8.2 schema 驗證發生在路由給擁有者之前
    const violations = validateValue(node.def.schema, incoming)
    if (violations.length > 0) {
      return { ok: false, error: schemaError(path, node.def, violations) }
    }

    const timeout = clamp(opts.timeout, DEFAULT_WRITE_TIMEOUT_MS, MAX_WRITE_TIMEOUT_MS)

    if (node.kernelOwned) {
      const outcome = this.handleKernelWrite(node, incoming)
      if ('code' in outcome) return { ok: false, error: outcome }
      return this.applyWriteOutcome(node, incoming, outcome)
    }

    const instance = node.instanceUid ? this.instances.get(node.instanceUid) : undefined
    if (!instance || !instance.live) {
      return { ok: false, error: noOwner(path) }
    }
    const handler = instance.writeHandler
    if (!handler) {
      return {
        ok: false,
        error: busError('E_NO_OWNER', `${path} 的擁有者未註冊寫入處理器`, {
          path,
          hint: '這是該 app 的實作缺陷，不是呼叫方的錯。此節點目前實質不可寫。',
        }),
      }
    }

    const guarded = await this.guard(
      async () => {
        try {
          // 誰發起的一併交給擁有者。這份資訊本來就在 $sys/activity 裡公開，
          // 不轉交只是讓想用它的 app 得繞一圈去比對，擋不住任何事。
          const outcome = await handler(node.relPath, incoming, opts.origin ?? null)
          return { ok: true as const, value: outcome }
        } catch (cause) {
          return {
            ok: false as const,
            error: busError('E_REJECTED', `擁有者處理寫入時拋出例外：${String(cause)}`, {
              path,
            }),
          }
        }
      },
      timeout,
      path,
      instance,
      'write',
    )

    if (!guarded.ok) return { ok: false, error: guarded.error }
    return this.applyWriteOutcome(node, incoming, guarded.value)
  }

  // ───────────────────────────────── Action（§9）

  async call(
    path: string,
    args: unknown = null,
    opts: { timeout?: number; origin?: string; gesture?: boolean } = {},
  ): Promise<CallResult> {
    const origin = opts.origin ?? null
    const hold = this.needsApproval(path, origin)
      ? await this.holdForApproval('call', path, args, origin)
      : null
    const result = hold ?? (await this.callInternal(path, args, opts))
    const compact = compactActivityValue(args)
    this.emitActivity({
      kind: 'call',
      path,
      value: compact.value,
      ...(compact.truncated ? { truncated: true } : {}),
      origin: opts.origin ?? null,
      gesture: opts.gesture === true,
      ok: result.ok,
      ...(result.ok ? {} : { error: result.error }),
      at: this.clock.now(),
    })
    return result
  }

  private async callInternal(
    path: string,
    args: unknown = null,
    opts: { timeout?: number; origin?: string } = {},
  ): Promise<CallResult> {
    const invalidPath = validatePath(path)
    if (invalidPath) return { ok: false, error: invalidPath }

    const node = this.nodes.get(path)
    if (!node) {
      return {
        ok: false,
        error: busError('E_NOT_FOUND', `節點不存在：${path}`, { path }),
      }
    }
    if (node.def.kind !== 'action') {
      return {
        ok: false,
        error: busError('E_UNSUPPORTED', `${path} 的 kind 是 ${node.def.kind}，不是 action`, {
          path,
          hint:
            node.def.kind === 'state' || node.def.kind === 'command'
              ? '請改用 write()。'
              : 'event 只能訂閱，不能呼叫。',
        }),
      }
    }

    const encoded = encodeValue(args, path)
    if (!encoded.ok) return { ok: false, error: encoded.error }
    const incoming = encoded.clone

    const violations = validateValue(node.def.schema, incoming)
    if (violations.length > 0) {
      return { ok: false, error: schemaError(path, node.def, violations) }
    }

    const timeout = clamp(opts.timeout, DEFAULT_CALL_TIMEOUT_MS, MAX_CALL_TIMEOUT_MS)

    if (node.kernelOwned) {
      return this.handleKernelCall(node, incoming, timeout)
    }

    const instance = node.instanceUid ? this.instances.get(node.instanceUid) : undefined
    if (!instance || !instance.live) return { ok: false, error: noOwner(path) }
    const handler = instance.callHandler
    if (!handler) {
      return {
        ok: false,
        error: busError('E_NO_OWNER', `${path} 的擁有者未註冊呼叫處理器`, {
          path,
          hint: '這是該 app 的實作缺陷，不是呼叫方的錯。',
        }),
      }
    }

    const guarded = await this.guard(
      async () => {
        try {
          const result = await handler(node.relPath, incoming, opts.origin ?? null)
          return { ok: true as const, value: result }
        } catch (cause) {
          return {
            ok: false as const,
            error: busError('E_REJECTED', String(cause), { path }),
          }
        }
      },
      timeout,
      path,
      instance,
      'call',
    )

    if (!guarded.ok) return { ok: false, error: guarded.error }

    const resultEncoded = encodeValue(guarded.value ?? null, path)
    if (!resultEncoded.ok) {
      return {
        ok: false,
        error: busError('E_INTERNAL', `${path} 的回傳值無法序列化`, {
          path,
          details: resultEncoded.error.message,
        }),
      }
    }
    return { ok: true, result: resultEncoded.clone }
  }

  // ───────────────────────────────── 註冊（§6）

  register(options: RegisterOptions): Registration {
    const { uid } = options
    const uidError = validatePath(uid)
    if (uidError || uid.includes('/') || uid.startsWith('$')) {
      throw fault('E_INVALID_PATH', `實例 uid "${uid}" 不合法`, {
        hint: 'uid 必須是單一路徑段，符合 [a-z0-9][a-z0-9_-]*，且不得以 $ 開頭。',
      })
    }

    // summary 是這個 app **對外唯一的一句話說明**——資訊面板顯示它，agent 從
    // registry 讀到的也是它。少了它，這個 app 對 agent 就是一個沒有標籤的按鈕。
    // 節點的 description 早就是必填的，理由一樣，這裡只是把同一條線畫到實例層。
    if (typeof options.summary !== 'string' || options.summary.trim().length === 0) {
      throw fault('E_SCHEMA', `實例 ${uid} 缺少 summary`, {
        hint: 'summary 是這個 app 對外的一句話說明，必填且不得為空——沒有它，agent 無從判斷該不該用它。',
      })
    }

    const existing = this.instances.get(uid)
    if (existing?.live) {
      throw fault('E_DUPLICATE', `實例 ${uid} 已存在且存活中`, {
        hint: '同一個 app 開兩次應使用不同的 uid。崩潰後重載才可沿用同一個 uid。',
      })
    }

    const prefix = `${APP_ROOT}/${uid}`
    const defs = options.nodes.map((def) => this.validateNodeDef(def, prefix))

    // 全有全無：先檢查重複，任一失敗就不留下任何痕跡
    const seen = new Set<string>()
    for (const def of defs) {
      const abs = `${prefix}/${def.path}`
      if (seen.has(abs)) {
        throw fault('E_DUPLICATE', `同一次註冊中重複的節點路徑：${def.path}`, { path: abs })
      }
      if (this.nodes.has(abs)) {
        throw fault('E_DUPLICATE', `節點已存在：${abs}`, { path: abs })
      }
      seen.add(abs)
    }

    const instance = existing ?? this.createInstanceRecord(uid, prefix, options)
    instance.title = options.title
    instance.summary = options.summary
    instance.groups = options.groups ?? {}
    instance.protocolVersion = options.protocol_version ?? PROTOCOL_VERSION
    instance.writeHandler = null
    instance.callHandler = null
    instance.live = true
    this.instances.set(uid, instance)

    if (existing === undefined) {
      this.installInstanceKernelNodes(instance, options.initial_window)
    }

    for (const def of defs) {
      const abs = `${prefix}/${def.path}`
      this.addNode({
        path: abs,
        relPath: def.path,
        def,
        instanceUid: uid,
        kernelOwned: false,
        hasValue: false,
        value: undefined,
        updated_at: 0,
        seq: 0,
      })
      instance.appNodes.add(abs)
    }

    // 節點全部建好之後才灌值：setValue 會通知訂閱者，而通知送出去的那一刻，
    // 這個 app 的樹應該已經是完整的
    for (const def of defs) {
      const seed = this.seedValue(instance, def)
      if (seed !== undefined) this.setValue(this.mustGet(`${prefix}/${def.path}`), seed)
    }
    // 存的值驗不過的那幾項**先留著**，不丟。有些節點的合法值要到執行期才知道——
    // 語音按鈕「連到哪一個語音元件」的 enum 是桌面上有哪些語音元件，重整剛開始時
    // 它們還沒載完，enum 只有 auto，存的選擇就驗不過。丟掉的話，使用者的選擇在
    // 重整那一刻就沒了；留著，等 replaceNodes 把 enum 補齊時再灌（見那裡）。
    instance.pendingSettings = this.unseeded(instance, defs)

    this.setLifecycle(instance, 'ready')
    this.refreshMeta(instance)
    this.refreshRegistry()

    return this.makeRegistration(instance)
  }

  /**
   * 宿主 API：在 app 實際註冊之前先建立視窗與 loading 狀態。
   * 之後 register() 使用同一個 uid 即接手該視窗。
   */
  declareLoading(options: {
    uid: string
    title: string
    summary?: string
    /** 載入的網址。session 存檔與 registry 都需要它。 */
    url?: string
    initial_window?: RegisterOptions['initial_window']
    /** 存檔裡這個實例調整過的 rw 節點，等它註冊時灌回去 */
    settings?: Record<string, unknown>
  }): void {
    if (this.instances.has(options.uid)) {
      throw fault('E_DUPLICATE', `實例 ${options.uid} 已存在`)
    }
    const prefix = `${APP_ROOT}/${options.uid}`
    const instance = this.createInstanceRecord(options.uid, prefix, {
      uid: options.uid,
      title: options.title,
      summary: options.summary ?? '',
      nodes: [],
    })
    instance.url = options.url ?? null
    instance.pendingSettings = options.settings ?? null
    this.instances.set(options.uid, instance)
    this.installInstanceKernelNodes(instance, options.initial_window)
    this.setLifecycle(instance, 'loading')
    this.refreshMeta(instance)
    this.refreshRegistry()
  }

  /** 宿主 API：偵測到 app 無回應。行為同註銷，但 lifecycle 為 crashed。 */
  markCrashed(uid: string, reason = 'app 無回應'): void {
    const instance = this.instances.get(uid)
    if (!instance) return
    this.teardownInstance(instance, 'crashed', reason)
  }

  /**
   * 宿主 API：完全移除一個實例，含 $window 與 $meta。
   * 這是 $sys/cmd/close 的行為，與 app 自行 unregister（留下墓碑視窗）不同。
   */
  destroyInstance(uid: string, reason = '已關閉'): void {
    const instance = this.instances.get(uid)
    if (!instance) return
    this.teardownInstance(instance, 'closed', reason)
    for (const path of instance.kernelNodes) this.removeNode(path, reason)
    instance.kernelNodes.clear()
    this.instances.delete(uid)
    this.refreshRegistry()
  }

  setLauncher(launcher: Launcher | null): void {
    this.launcher = launcher
  }

  // ───────────────────────────────  風險閘門（§9.x）

  /**
   * 攔下需要人類確認的操作，直到有人同意、拒絕或逾時。
   *
   * 回傳 null 表示放行；回傳錯誤表示這次操作到此為止，**完全沒有執行**。
   *
   * 這是「機制，不是政策」的實例：核心不判斷什麼危險——危險與否由 app 在
   * manifest 裡宣告（`risk`），門檻由使用者設定（`$sys/gate/level`），
   * 核心只負責在門檻成立時把人叫出來。
   */
  private holdForApproval(
    kind: 'write' | 'call',
    path: string,
    value: unknown,
    origin: string | null,
  ): Promise<{ ok: false; error: BusError } | null> {
    const node = this.nodes.get(path)
    const id = `g${this.nextGateId++}`
    const entry: PendingApproval = {
      id,
      kind,
      path,
      value: compactActivityValue(value).value,
      origin: origin as string,
      risk: node?.def.risk ?? 'none',
      description: node?.def.description ?? '',
      at: this.clock.now(),
    }

    const timeoutMs = clamp(
      this.nodes.get(`${SYS_ROOT}/gate/timeout_ms`)?.value as number | undefined,
      DEFAULT_GATE_TIMEOUT_MS,
      MAX_GATE_TIMEOUT_MS,
    )

    return new Promise<{ ok: false; error: BusError } | null>((resolve) => {
      const timer = setTimeout(() => {
        this.settleGate(id, {
          ok: false,
          error: busError('E_TIMEOUT', `等待人類確認逾時（${timeoutMs} ms）：${path}`, {
            path,
            hint: '這個操作**沒有執行**。被閘門攔下的操作以 $sys/gate/timeout_ms 為準，不是呼叫方指定的逾時。',
          }),
        })
      }, timeoutMs)

      this.gates.set(id, { entry, resolve, timer })
      this.publishPending()
    })
  }

  private needsApproval(path: string, origin: string | null): boolean {
    // 桌面自己的動作不攔——人按下按鈕本身就是同意
    if (origin === null) return false
    // 閘門自己的節點永遠不攔，否則會鎖死
    if (path.startsWith(`${SYS_ROOT}/gate/`)) return false
    if (path === `${SYS_ROOT}/cmd/approve` || path === `${SYS_ROOT}/cmd/deny`) return false

    const level = this.nodes.get(`${SYS_ROOT}/gate/level`)?.value as GateLevel | undefined
    if (level === undefined || level === 'off') return false

    const exempt = (this.nodes.get(`${SYS_ROOT}/gate/exempt`)?.value ?? []) as string[]
    if (exempt.includes(origin)) return false

    const risk = this.nodes.get(path)?.def.risk ?? 'none'
    if (risk === 'high') return true
    return level === 'low' && risk === 'low'
  }

  private settleGate(id: string, outcome: { ok: false; error: BusError } | null): boolean {
    const gate = this.gates.get(id)
    if (!gate) return false
    this.gates.delete(id)
    clearTimeout(gate.timer)
    gate.resolve(outcome)
    this.publishPending()
    return true
  }

  private publishPending(): void {
    const node = this.nodes.get(`${SYS_ROOT}/gate/pending`)
    if (!node) return
    this.setValue(
      node,
      [...this.gates.values()].map((gate) => gate.entry),
    )
  }

  /**
   * 把一次嘗試發布成 `$sys/activity` 事件。
   *
   * 刻意用一般的 event 而不是私有的觀測 hook——原則一要求
   * 「不在 topic 樹裡的東西，不存在」。這樣 iframe 裡的 app
   * 與遠端 agent 也看得到，而不只有主頁面。
   */
  private emitActivity(activity: Activity): void {
    const node = this.nodes.get(`${SYS_ROOT}/activity`)
    if (!node) return
    this.deliver({ type: 'event', path: node.path, payload: activity, at: activity.at })
  }

  /** 宿主 API：設定「有什麼可以開」。這是 $sys/cmd/launch 唯一認可的來源。 */
  setCatalog(entries: readonly CatalogEntry[]): void {
    this.catalog = [...entries]
    const node = this.nodes.get(`${SYS_ROOT}/registry/apps`)
    if (node) this.setValue(node, this.catalog)
  }

  /** 宿主 API：設定可選的版面板型。格子座標是 0–1 的比例。 */
  setLayouts(templates: readonly LayoutTemplate[]): void {
    this.layouts = [...templates]
    const node = this.nodes.get(`${SYS_ROOT}/layout/templates`)
    if (node) this.setValue(node, this.layouts)
  }

  /** 宿主 API：回報桌面可用區域。視窗縮放時要更新。 */
  setViewport(size: Viewport): void {
    const node = this.nodes.get(`${SYS_ROOT}/viewport`)
    if (!node) return
    const next = { w: Math.round(size.w), h: Math.round(size.h) }
    // 一樣就不寫，跟 setVisible 同一個道理。ResizeObserver 會為了各種理由觸發，
    // 而每一次寫入都會叫醒所有訂閱者——包括會因此排一次存檔的那些。
    const current = node.value as Viewport | undefined
    if (node.hasValue && current?.w === next.w && current?.h === next.h) return
    this.setValue(node, next)
  }

  /**
   * 宿主 API：回報桌面現在看不看得見。
   *
   * 看不見的時候瀏覽器會節流計時器，這是一個隱形的限制——
   * 做成 topic 之後它就跟別的東西一樣可觀察，app 與 agent 才知道
   * 「現在不要依賴輪詢」。原則一：不在 topic 樹裡的東西，不存在。
   */
  setVisible(visible: boolean): void {
    const node = this.nodes.get(`${SYS_ROOT}/visible`)
    if (!node) return
    if (node.hasValue && node.value === visible) return
    this.setValue(node, visible)
  }


  getCatalog(): CatalogEntry[] {
    return [...this.catalog]
  }

  listInstances(): InstanceSummary[] {
    return [...this.instances.values()].map((instance) => ({
      uid: instance.uid,
      title: instance.title,
      summary: instance.summary,
      lifecycle: instance.lifecycle,
      node_count: instance.appNodes.size + instance.kernelNodes.size,
      url: instance.url,
    }))
  }

  /**
   * 目前桌面的可序列化現況。純粹從 topic 推導出來，沒有另一份平行狀態。
   *
   * 只含桌面自己的東西：開了哪些 app、視窗在哪、外觀。
   * App 的內部資料不在這裡——那是 app 與其後端的責任（concept.md §7.1）。
   */
  sessionSnapshot(): SessionSnapshot {
    const instances: SessionInstance[] = []
    for (const instance of this.instances.values()) {
      if (!instance.url) continue
      const rect = this.nodes.get(`${instance.prefix}/$window/rect`)?.value as
        | WindowRect
        | undefined
      if (!rect) continue
      instances.push({
        uid: instance.uid,
        url: instance.url,
        title: instance.title,
        rect,
        state:
          (this.nodes.get(`${instance.prefix}/$window/state`)?.value as WindowState) ?? 'normal',
        z: (this.nodes.get(`${instance.prefix}/$window/z`)?.value as number) ?? 0,
        transparent: this.nodes.get(`${instance.prefix}/$window/transparent`)?.value === true,
        locked: this.nodes.get(`${instance.prefix}/$window/locked`)?.value === true,
        ...this.settingsOf(instance),
      })
    }
    instances.sort((a, b) => a.z - b.z)

    return {
      version: 1,
      saved_at: this.clock.now(),
      locale: (this.nodes.get(`${SYS_ROOT}/locale`)?.value as string) ?? null,
      screen: (this.nodes.get(`${SYS_ROOT}/screen`)?.value as string) ?? null,
      wallpaper: {
        color: this.nodes.get(`${SYS_ROOT}/wallpaper/color`)?.value ?? null,
        image: this.nodes.get(`${SYS_ROOT}/wallpaper/image`)?.value ?? null,
      },
      layout: (this.nodes.get(`${SYS_ROOT}/layout/template`)?.value as string) ?? 'free',
      instances,
    }
  }

  /**
   * 一個實例被調整過的設定。
   *
   * 三條界線：
   *   - 只收 `rw` 的 state。`r` 是 app 自己算出來的，存它等於存一份會被立刻
   *     覆蓋的影子；`$window/*` 已經有自己的欄位了。
   *   - 和出廠值一樣就不存。app 之後改預設時，沒動過那一項的桌面才跟得上；
   *     全部照存的話，使用者從沒選過的東西會被凍在舊版本。
   *   - 還沒有值的不存。「沒設過」和「設成 null」不一樣。
   */
  private settingsOf(instance: InstanceRecord): { settings?: Record<string, unknown> } {
    const settings: Record<string, unknown> = {}
    for (const abs of instance.appNodes) {
      const node = this.nodes.get(abs)
      if (!node?.hasValue) continue
      if (node.def.kind !== 'state' || node.def.access !== 'rw') continue
      if (node.def.initial !== undefined && same(node.value, node.def.initial)) continue
      settings[node.relPath] = node.value
    }
    // 還在等 enum 補齊的存檔值也要存回去：不然在那之前存一次檔，使用者的選擇就
    // 從存檔裡消失了，而且看不出來。
    for (const [rel, value] of Object.entries(instance.pendingSettings ?? {})) {
      if (!(rel in settings)) settings[rel] = value
    }
    return Object.keys(settings).length > 0 ? { settings } : {}
  }

  /** 註冊時驗不過、因此還沒灌進去的存檔值。 */
  private unseeded(instance: InstanceRecord, defs: NodeDef[]): Record<string, unknown> | null {
    const left: Record<string, unknown> = {}
    for (const def of defs) {
      const saved = instance.pendingSettings?.[def.path]
      if (saved === undefined || def.kind !== 'state' || def.access !== 'rw') continue
      if (validateValue(def.schema, saved).length > 0) left[def.path] = saved
    }
    return Object.keys(left).length > 0 ? left : null
  }

  /**
   * 節點換了定義之後，再試一次還在等的存檔值。
   *
   * 只在節點還停在出廠值（或沒有值）時灌：這段期間有人選過別的，那是比存檔更新
   * 的決定，存的那個就作廢。驗得過就灌、驗不過就繼續等——下一次 replaceNodes
   * 也許就補齊了。灌值不經 onWrite（跟註冊時的還原一樣），app 要自己讀一次。
   */
  private retryPending(instance: InstanceRecord, node: NodeRecord, def: NodeDef, initial: unknown): void {
    const pending = instance.pendingSettings
    if (!pending || !(def.path in pending)) return
    const saved = pending[def.path]
    const untouched = !node.hasValue || (initial !== undefined && same(node.value, initial))
    if (!untouched) {
      delete pending[def.path]
    } else if (validateValue(def.schema, saved).length === 0) {
      this.setValue(node, saved)
      delete pending[def.path]
    }
    if (Object.keys(pending).length === 0) instance.pendingSettings = null
  }

  // ───────────────────────────────── Registration

  private makeRegistration(instance: InstanceRecord): Registration {
    const bus = this
    return {
      get uid() {
        return instance.uid
      },

      publish(relPath: string, value: unknown): void {
        const node = bus.ownedNode(instance, relPath)
        if (node.def.kind !== 'state') {
          throw fault('E_UNSUPPORTED', `${node.path} 的 kind 是 ${node.def.kind}，不能 publish`, {
            path: node.path,
            hint: 'event 請用 emit()；command 與 action 沒有保留值。',
          })
        }
        const encoded = encodeValue(value, node.path)
        if (!encoded.ok) throw fault(encoded.error.code, encoded.error.message, { path: node.path })
        const violations = validateValue(node.def.schema, encoded.clone)
        if (violations.length > 0) {
          const err = schemaError(node.path, node.def, violations)
          throw fault(err.code, err.message, { path: node.path, details: err.details })
        }
        bus.setValue(node, encoded.clone)
      },

      emit(relPath: string, payload: unknown): void {
        const node = bus.ownedNode(instance, relPath)
        if (node.def.kind !== 'event') {
          throw fault('E_UNSUPPORTED', `${node.path} 的 kind 是 ${node.def.kind}，不能 emit`, {
            path: node.path,
          })
        }
        const encoded = encodeValue(payload, node.path)
        if (!encoded.ok) throw fault(encoded.error.code, encoded.error.message, { path: node.path })
        const violations = validateValue(node.def.schema, encoded.clone)
        if (violations.length > 0) {
          const err = schemaError(node.path, node.def, violations)
          throw fault(err.code, err.message, { path: node.path, details: err.details })
        }
        bus.deliver({
          type: 'event',
          path: node.path,
          payload: encoded.clone,
          at: bus.clock.now(),
        })
      },

      onWrite(handler: WriteHandler): void {
        instance.writeHandler = handler
      },

      onCall(handler: CallHandler): void {
        instance.callHandler = handler
      },

      addNodes(nodes: NodeDef[]): void {
        if (!instance.live) throw fault('E_NO_OWNER', `實例 ${instance.uid} 已結束`)
        const defs = nodes.map((def) => bus.validateNodeDef(def, instance.prefix))
        for (const def of defs) {
          const abs = `${instance.prefix}/${def.path}`
          if (bus.nodes.has(abs)) throw fault('E_DUPLICATE', `節點已存在：${abs}`, { path: abs })
        }
        for (const def of defs) {
          const abs = `${instance.prefix}/${def.path}`
          bus.addNode({
            path: abs,
            relPath: def.path,
            def,
            instanceUid: instance.uid,
            kernelOwned: false,
            hasValue: false,
            value: undefined,
            updated_at: 0,
            seq: 0,
          })
          instance.appNodes.add(abs)
        }
        bus.refreshMeta(instance)
        bus.refreshRegistry()
      },

      /**
       * 換掉已存在節點的定義，原地、原子、保留現值。
       *
       * 為什麼需要它：有些節點的合法值要到執行期才知道——「可以選哪一個代理人」
       * 得先跟後端要清單。這種時候 app 應該把 `schema.enum` 換成真正的清單，
       * 而不是另外開一條路徑去講「選項在那邊」。agent 需要的是**有哪些合法值**，
       * 不是**去哪裡找合法值**，而前者在樹裡早就有標準說法了。
       *
       * 用 remove + add 做不到：中間有一段那個節點不存在，訂閱者會先收到「工具
       * 不見了」再收到「多了新工具」，值也掉了。對「同一個節點換了合法範圍」
       * 來說那是錯的敘述。
       */
      replaceNodes(nodes: NodeDef[]): void {
        if (!instance.live) throw fault('E_NO_OWNER', `實例 ${instance.uid} 已結束`)
        const defs = nodes.map((def) => bus.validateNodeDef(def, instance.prefix))
        for (const def of defs) {
          const abs = `${instance.prefix}/${def.path}`
          const existing = bus.nodes.get(abs)
          if (!existing || !instance.appNodes.has(abs)) {
            throw fault('E_NOT_FOUND', `節點不存在，無法取代：${def.path}`, {
              path: abs,
              hint: '取代只能用在這個實例已經註冊過的節點上。新增請用 add_nodes。',
            })
          }
          // kind 換掉就不是同一個節點了——state 變成 action 是「換一個東西」，
          // 不是「更新」。access 可以換：「這個東西現在唯讀了」是合理的執行期狀態。
          if (existing.def.kind !== def.kind) {
            throw fault('E_SCHEMA', `不得變更 ${def.path} 的 kind`, {
              path: abs,
              hint: `原本是 ${existing.def.kind}，收到 ${def.kind}。kind 不同就是另一個節點，請 remove 再 add。`,
            })
          }
        }
        for (const def of defs) {
          const abs = `${instance.prefix}/${def.path}`
          const node = bus.nodes.get(abs)
          if (!node) continue
          // A replacement often leaves initial out (it is only widening the enum);
          // the factory value is still the old one for "has anybody chosen yet".
          const initial = def.initial !== undefined ? def.initial : node.def.initial
          node.def = def
          // 現值若已經不符合新的 schema（例如選中的代理人被刪掉了，不在新的
          // enum 裡），就把它清掉而不是留著。留著等於讓樹說一句不成立的話；
          // 清掉是誠實的「現在沒有值」，而 app 隨後可以補上一個合法的。
          if (node.hasValue && validateValue(def.schema, node.value).length > 0) {
            node.hasValue = false
            node.value = undefined
          }
          bus.retryPending(instance, node, def, initial)
        }
        bus.refreshMeta(instance)
        bus.refreshRegistry()
      },

      removeNodes(relPaths: string[]): void {
        for (const rel of relPaths) {
          const abs = `${instance.prefix}/${rel}`
          if (!instance.appNodes.has(abs)) continue
          bus.removeNode(abs, '節點已撤下')
          instance.appNodes.delete(abs)
        }
        bus.refreshMeta(instance)
        bus.refreshRegistry()
      },

      unregister(reason?: string): void {
        bus.teardownInstance(instance, 'closed', reason ?? 'app 自行結束')
      },
    }
  }

  // ───────────────────────────────── 內部：節點

  private installSysNodes(): void {
    for (const def of SYS_NODES) {
      this.addNode({
        path: def.path,
        relPath: def.path,
        def,
        instanceUid: null,
        kernelOwned: true,
        hasValue: false,
        value: undefined,
        updated_at: 0,
        seq: 0,
      })
    }
    this.setValue(this.mustGet('$sys/registry/instances'), [])
    this.setValue(this.mustGet('$sys/registry/apps'), [])
    this.setValue(this.mustGet('$sys/visible'), true)
    // 預設不攔截：什麼算危險是使用者的政策，核心不替他決定
    this.setValue(this.mustGet('$sys/gate/level'), 'off')
    this.setValue(this.mustGet('$sys/gate/exempt'), ['shell'])
    this.setValue(this.mustGet('$sys/gate/timeout_ms'), DEFAULT_GATE_TIMEOUT_MS)
    this.setValue(this.mustGet('$sys/gate/pending'), [])
    this.setValue(this.mustGet('$sys/viewport'), { w: 0, h: 0 })
    this.setValue(this.mustGet('$sys/layout/templates'), [])
    this.setValue(this.mustGet('$sys/layout/template'), 'free')
    // 出廠值是一個常數。「跟著瀏覽器語言」是宿主的政策，而且 navigator 是 DOM
    // ——核心碰不得（boundaries.test.ts）。shell 會在沒有存檔時覆蓋它。
    this.setValue(this.mustGet('$sys/locale'), 'zh-Hant')
    this.setValue(this.mustGet('$sys/screen'), null)
    this.setValue(this.mustGet('$sys/wallpaper/color'), '#1a1a2e')
    this.setValue(this.mustGet('$sys/wallpaper/image'), null)
    this.setValue(this.mustGet('$sys/menu/at'), null)
  }

  private createInstanceRecord(
    uid: string,
    prefix: string,
    options: RegisterOptions,
  ): InstanceRecord {
    return {
      uid,
      prefix,
      url: null,
      title: options.title,
      summary: options.summary,
      groups: options.groups ?? {},
      protocolVersion: options.protocol_version ?? PROTOCOL_VERSION,
      lifecycle: 'loading',
      appNodes: new Set(),
      kernelNodes: new Set(),
      writeHandler: null,
      callHandler: null,
      pending: new Set(),
      live: false,
      pendingSettings: null,
    }
  }

  /**
   * 把一個建議的視窗放進看得見的範圍裡。
   *
   * 目錄裡的 `default_window` 是替一塊大螢幕挑的位置——`x: 760` 在一台 390 寬的
   * 手機上整個落在畫面外。而視窗超出畫布時畫布會跟著長（那是刻意的，排到外面的
   * 視窗才找得回來），所以那支 app **確實開起來了**，只是在一個要橫向捲很久才
   * 看得到的地方。症狀是「按了新增，什麼都沒出現」。
   *
   * 所以先縮再挪：太寬就縮到畫布內（留一點邊），然後把整個框推回範圍裡。順序反
   * 過來的話，一個比畫布還寬的視窗會被推到負座標。
   *
   * 只在**開的時候**做一次。之後人要把視窗拖到畫布外是他的自由——那是「桌面比
   * 螢幕大」這件事本來就允許的，而這裡修的是「它從來沒有出現過」。
   */
  private fitToViewport(rect: WindowRect): WindowRect {
    const view = this.nodes.get(`${SYS_ROOT}/viewport`)?.value as Viewport | undefined
    if (!view || view.w <= 0 || view.h <= 0) return rect
    const margin = 8
    const w = Math.min(rect.w, Math.max(120, view.w - margin * 2))
    const h = Math.min(rect.h, Math.max(80, view.h - margin * 2))
    return {
      x: Math.max(margin, Math.min(rect.x, view.w - w - margin)),
      y: Math.max(margin, Math.min(rect.y, view.h - h - margin)),
      w,
      h,
    }
  }

  /**
   * 一塊手機大小的螢幕上，視窗的左右由桌面決定，不由寫的人決定。
   *
   * 一欄。x 與 w 是算出來的，誰寫都一樣——代理人、拖曳、還原都一樣。y 與 h 不
   * 碰：**桌面可以比螢幕高**，那是這裡刻意不去管的另一件事（見 D38）。
   *
   * 為什麼是核心管而不是寫的人自己算：393 寬的螢幕上，「貼齊左右各留 8」不是
   * 一種風格，它是唯一能看的排法。把它交給每一個寫入者，等於要每一個人都算對
   * 一次同樣的算術，而算錯的樣子是視窗貼死邊、圓角與陰影被切掉——**看起來像
   * 壞了，不像擺錯了**。實測下來，同一個代理人、同一份說明，這一步時對時錯。
   *
   * 只在窄螢幕上。寬一點的固定螢幕（平板、看板）放得下兩欄，那裡一欄反而是損
   * 失，所以門檻畫在 COLUMN_MAX_W：低於它，兩個 240 寬的元件並排會擠成一團。
   *
   * $sys/screen 是 null（跟著瀏覽器視窗）時什麼都不做——那是電腦，人有滑鼠、
   * 有空間，位置是他的自由。
   */
  private snapToColumn(rect: WindowRect): WindowRect {
    const screen = this.nodes.get(`${SYS_ROOT}/screen`)?.value
    if (typeof screen !== 'string') return rect
    const width = Number(screen.split('x')[0])
    if (!Number.isFinite(width) || width <= 0 || width > COLUMN_MAX_W) return rect
    const inner = Math.max(120, width - COLUMN_MARGIN * 2)
    return { ...rect, x: COLUMN_MARGIN, w: inner }
  }

  private installInstanceKernelNodes(
    instance: InstanceRecord,
    initial: RegisterOptions['initial_window'],
  ): void {
    for (const def of INSTANCE_KERNEL_NODES) {
      const abs = `${instance.prefix}/${def.path}`
      this.addNode({
        path: abs,
        relPath: def.path,
        def,
        instanceUid: instance.uid,
        kernelOwned: true,
        hasValue: false,
        value: undefined,
        updated_at: 0,
        seq: 0,
      })
      instance.kernelNodes.add(abs)
    }
    const wanted = { ...DEFAULT_RECT, ...(initial?.rect ?? {}) }
    // fit: false＝這個位置是決定過的，不是建議（還原就是這種）。見 InitialWindow.fit。
    // fit:false 擋掉的是「夾進可見範圍」，不是「排成一欄」——後者在窄螢幕上
    // 對還原一樣成立：在電腦上存的那份版面，在手機上打開也該是能看的。
    const rect = this.snapToColumn(initial?.fit === false ? wanted : this.fitToViewport(wanted))
    this.setValue(this.mustGet(`${instance.prefix}/$window/rect`), rect)
    this.setValue(this.mustGet(`${instance.prefix}/$window/state`), initial?.state ?? 'normal')
    this.setValue(this.mustGet(`${instance.prefix}/$window/z`), initial?.z ?? 0)
    this.setValue(
      this.mustGet(`${instance.prefix}/$window/transparent`),
      initial?.transparent ?? false,
    )
    this.setValue(this.mustGet(`${instance.prefix}/$window/locked`), false)
  }

  private validateNodeDef(def: NodeDef, prefix: string): NodeDef {
    const abs = `${prefix}/${def.path}`
    const invalid = validatePath(abs)
    if (invalid) throw fault(invalid.code, invalid.message, { path: abs })

    if (hasReservedSegment(def.path)) {
      throw fault('E_RESERVED', `不得註冊 $ 開頭的節點：${def.path}`, {
        path: abs,
        hint: '$ 開頭的路徑段一律由核心擁有（$meta、$lifecycle、$window/…）。',
      })
    }
    if (typeof def.description !== 'string' || def.description.trim().length === 0) {
      throw fault('E_SCHEMA', `節點 ${def.path} 缺少 description`, {
        path: abs,
        hint: 'description 是 agent 唯一的理解來源，必填且不得為空。',
      })
    }
    if (def.schema === undefined || def.schema === null) {
      throw fault('E_SCHEMA', `節點 ${def.path} 缺少 schema`, { path: abs })
    }
    const comboError = checkKindAccess(def.kind, def.access, abs)
    if (comboError) throw fault(comboError.code, comboError.message, { path: abs, hint: comboError.hint })

    return def
  }

  private addNode(record: NodeRecord): void {
    this.nodes.set(record.path, record)
    this.deliver({ type: 'register', path: record.path, def: record.def })
  }

  private removeNode(path: string, reason: string): void {
    if (!this.nodes.delete(path)) return
    this.deliver({ type: 'retract', path, reason })
  }

  /**
   * 註冊時一個節點應該帶什麼值：有存檔就用存檔，沒有就用出廠值。
   *
   * 存檔可能比 app 舊——schema 收緊了，或那個選項已經沒了。驗不過就當作沒存過
   * 而不是硬灌：把不合法的值放進樹裡，等於讓 schema 說謊，而 agent 只有 schema
   * 可以信。
   */
  private seedValue(instance: InstanceRecord, def: NodeDef): unknown {
    if (def.kind !== 'state') return undefined
    if (def.access === 'rw') {
      const saved = instance.pendingSettings?.[def.path]
      if (saved !== undefined && validateValue(def.schema, saved).length === 0) return saved
    }
    return def.initial
  }

  private mustGet(path: string): NodeRecord {
    const node = this.nodes.get(path)
    if (!node) throw fault('E_INTERNAL', `核心節點遺失：${path}`, { path })
    return node
  }

  private ownedNode(instance: InstanceRecord, relPath: string): NodeRecord {
    const abs = `${instance.prefix}/${relPath}`
    if (!instance.appNodes.has(abs)) {
      throw fault('E_NOT_FOUND', `${instance.uid} 未擁有節點 ${relPath}`, {
        path: abs,
        hint: '只能對自己註冊的節點 publish / emit。$ 開頭的節點由核心擁有。',
      })
    }
    return this.mustGet(abs)
  }

  private setValue(node: NodeRecord, value: unknown): void {
    node.value = value
    node.hasValue = true
    node.updated_at = this.clock.now()
    node.seq = ++this.seqCounter
    this.deliver({
      type: 'state',
      path: node.path,
      value: node.value,
      updated_at: node.updated_at,
      seq: node.seq,
      retained: false,
    })
  }

  private snapshot(node: NodeRecord): StateSnapshot {
    const maxAge = node.def.max_age
    const stale =
      typeof maxAge === 'number' && maxAge >= 0
        ? this.clock.now() - node.updated_at > maxAge
        : false
    return {
      path: node.path,
      value: node.value,
      updated_at: node.updated_at,
      seq: node.seq,
      stale,
    }
  }

  // ───────────────────────────────── 內部：實例生命週期

  private setLifecycle(instance: InstanceRecord, lifecycle: Lifecycle): void {
    instance.lifecycle = lifecycle
    const node = this.nodes.get(`${instance.prefix}/$lifecycle`)
    if (node) this.setValue(node, lifecycle)
  }

  private teardownInstance(
    instance: InstanceRecord,
    lifecycle: Lifecycle,
    reason: string,
  ): void {
    if (!instance.live && instance.lifecycle === lifecycle) return

    // 調整過的設定跟著實例活下來，等它重新註冊時灌回去。§6.2 已經說 `$window/*`
    // 重載後沿用不重設——重載後視窗回到原位、卻忘記自己在看哪個 topic，是同一個
    // 承諾只兌現一半。
    instance.pendingSettings = this.settingsOf(instance).settings ?? null

    // §6.2 所有非 $ 節點立即失效，$window 與 $meta 保留
    for (const path of instance.appNodes) this.removeNode(path, reason)
    instance.appNodes.clear()

    instance.live = false
    instance.writeHandler = null
    instance.callHandler = null

    // 進行中的呼叫立即以 E_NO_OWNER 失敗
    const pending = [...instance.pending]
    instance.pending.clear()
    for (const abort of pending) {
      abort(busError('E_NO_OWNER', `擁有者已消失：${reason}`, { path: instance.prefix }))
    }

    // 這個 app 送出的、還在等人確認的操作也要清掉——
    // 沒有人會想批准一個已經不存在的 app 提出的請求
    for (const [id, gate] of [...this.gates]) {
      if (gate.entry.origin !== instance.uid) continue
      this.settleGate(id, {
        ok: false,
        error: busError('E_NO_OWNER', `發起者已消失：${reason}`, { path: gate.entry.path }),
      })
    }

    this.setLifecycle(instance, lifecycle)
    this.refreshMeta(instance)
    this.refreshRegistry()
  }

  private refreshMeta(instance: InstanceRecord): void {
    const node = this.nodes.get(`${instance.prefix}/$meta`)
    if (!node) return
    const paths = [...instance.kernelNodes, ...instance.appNodes].sort()
    const meta: InstanceMeta = {
      uid: instance.uid,
      title: instance.title,
      summary: instance.summary,
      protocol_version: instance.protocolVersion,
      groups: instance.groups,
      nodes: paths
        .map((path) => this.nodes.get(path))
        .filter((n): n is NodeRecord => n !== undefined)
        .map((n) => n.def),
    }
    // 同上：$meta 也是導出來的，重算出同一份就不必吵醒任何人。訂 $meta 的是來源
    // 選單那類東西，它們每收到一次就重掃一次全桌面。
    if (node.hasValue && same(node.value, meta)) return
    this.setValue(node, meta)
  }

  /**
   * 重算執行中的實例清單。
   *
   * **算出來一樣就不發布。** 這個節點是**導出來的**——每一次 addNodes、
   * replaceNodes、生命週期變化都會重算它一遍，而其中大部分算出來的是同一份清單
   * （摘要裡只有 uid、標題、節點數這些）。照發的話，訂閱者會被一個沒有變化的
   * 「變化」叫醒;桌面的自動存檔就訂著這一條，於是變成每隔幾秒存一次檔——而畫面
   * 上什麼都沒動過。
   *
   * 一條 state topic 該發布的是**變化**。「我重算了一次」不是變化。
   */
  private refreshRegistry(): void {
    const node = this.nodes.get('$sys/registry/instances')
    if (!node) return
    const next = this.listInstances()
    if (node.hasValue && same(node.value, next)) return
    this.setValue(node, next)
  }

  // ───────────────────────────────── 內部：核心擁有節點的讀寫

  private handleKernelWrite(node: NodeRecord, incoming: unknown): WriteOutcome | BusError {
    if (
      node.path === '$sys/wallpaper/color' ||
      node.path === '$sys/wallpaper/image' ||
      node.path === '$sys/gate/level' ||
      node.path === '$sys/gate/exempt' ||
      node.path === '$sys/gate/timeout_ms' ||
      // 選單位置：shell 寫入代表「人在這裡按了右鍵」，接手的 app 寫 null 收起。
      // 核心不知道會跳出什麼，也不需要知道——它只保管這個座標。
      node.path === '$sys/menu/at' ||
      // 語言與畫布尺寸是**桌面的外觀**，跟桌布同一類：宿主套用，但決定它的是人
      // （或代理人）。宣告成 rw 是不夠的——$sys 底下預設一律不可寫，這份清單才
      // 是真正的門，而漏掉這一行的症狀是設定面板上一顆按不動的按鈕。
      node.path === '$sys/locale' ||
      node.path === '$sys/screen'
    ) {
      return { accept: true }
    }
    if (node.path === '$sys/layout/template') {
      const id = String(incoming)
      if (id !== 'free' && !this.layouts.some((template) => template.id === id)) {
        return busError('E_NOT_FOUND', `沒有這個板型：${id}`, {
          path: node.path,
          hint: '可用的板型請讀 $sys/layout/templates。"free" 表示不吸附。',
          details: { available: ['free', ...this.layouts.map((t) => t.id)] },
        })
      }
      return { accept: true }
    }
    if (node.relPath === '$window/rect') {
      // 釘住就是真的動不了，**不分來源**。只在 shell 那邊藏起拖曳把手的話，
      // agent 照樣搬得走——那不是鎖，是裝飾。想移動請先解鎖。
      const uid = node.path.split('/')[1]
      if (this.nodes.get(`app/${uid}/$window/locked`)?.value === true) {
        return busError('E_DENIED', `${node.path} 已鎖定`, {
          path: node.path,
          hint: `先把 app/${uid}/$window/locked 設為 false 再移動。`,
        })
      }
      const current = (node.hasValue ? node.value : DEFAULT_RECT) as WindowRect
      const merged = this.snapToColumn({
        ...current,
        ...(incoming as Partial<WindowRect>),
      })
      const violations = validateValue(node.def.schema, merged)
      if (violations.length > 0) return schemaError(node.path, node.def, violations)
      return { accept: true, value: merged }
    }
    if (
      node.relPath === '$window/state' ||
      node.relPath === '$window/z' ||
      node.relPath === '$window/locked' ||
      node.relPath === '$window/transparent'
    ) {
      return { accept: true }
    }
    return busError('E_NOT_WRITABLE', `${node.path} 由核心擁有且不可寫`, { path: node.path })
  }

  private async handleKernelCall(
    node: NodeRecord,
    args: unknown,
    timeout: number,
  ): Promise<CallResult> {
    if (node.path === '$sys/cmd/close') {
      const uid = (args as { uid: string }).uid
      if (!this.instances.has(uid)) {
        return {
          ok: false,
          error: busError('E_NOT_FOUND', `實例不存在：${uid}`, {
            hint: '可用的 uid 請讀 $sys/registry/instances。',
          }),
        }
      }
      this.destroyInstance(uid)
      return { ok: true, result: { ok: true } }
    }

    if (node.path === '$sys/cmd/approve' || node.path === '$sys/cmd/deny') {
      const id = String((args as { id: string }).id)
      const denied = node.path.endsWith('deny')
      const outcome = denied
        ? {
            ok: false as const,
            error: busError(
              'E_DENIED',
              String((args as { reason?: string }).reason ?? '人類拒絕了這個操作'),
              {
                hint: '這個操作沒有執行。請依拒絕原因調整後再試，不要原封不動重送。',
              },
            ),
          }
        : null

      if (!this.settleGate(id, outcome)) {
        return {
          ok: false,
          error: busError('E_NOT_FOUND', `沒有待確認的操作：${id}`, {
            hint: '它可能已經被處理或逾時了。目前待確認的清單請讀 $sys/gate/pending。',
          }),
        }
      }
      return { ok: true, result: { ok: true } }
    }

    if (node.path === '$sys/cmd/snap') {
      return this.handleSnap(args as { uid: string; cell?: number; template?: string })
    }

    if (node.path === '$sys/cmd/launch') {
      const launcher = this.launcher
      if (!launcher) {
        return {
          ok: false,
          error: busError('E_UNSUPPORTED', '此環境未安裝 launcher，無法開啟新的 app', {
            path: node.path,
            hint: '需由宿主呼叫 bus.setLauncher() 安裝。',
          }),
        }
      }

      const appId = (args as { app_id: string }).app_id
      const entry = this.catalog.find((candidate) => candidate.app_id === appId)
      if (!entry) {
        return {
          ok: false,
          error: busError('E_NOT_FOUND', `目錄中沒有 app_id：${appId}`, {
            path: node.path,
            hint: '可開啟的 app 請讀 $sys/registry/apps。不能指定任意網址。',
            details: { available: this.catalog.map((candidate) => candidate.app_id) },
          }),
        }
      }

      const guarded = await this.guard(
        async () => {
          try {
            const { app_id: _ignored, ...placement } = args as { app_id: string } & InitialWindow
            const result = await launcher(entry, placement)
            return { ok: true as const, value: result }
          } catch (cause) {
            return { ok: false as const, error: busError('E_REJECTED', String(cause), { path: node.path }) }
          }
        },
        timeout,
        node.path,
        null,
        'call',
      )
      if (!guarded.ok) return { ok: false, error: guarded.error }
      return { ok: true, result: guarded.value }
    }

    return {
      ok: false,
      error: busError('E_INTERNAL', `未實作的核心 action：${node.path}`, { path: node.path }),
    }
  }

  /**
   * 把視窗放進版面格子。
   *
   * 格子是 0–1 的比例，所以換算成像素這件事必須有人做——由核心做，
   * 因為視窗幾何本來就是核心的職責，而且這樣 agent 就不必自己算像素。
   */
  private async handleSnap(args: {
    uid: string
    cell?: number
    template?: string
  }): Promise<CallResult> {
    const instance = this.instances.get(args.uid)
    if (!instance) {
      return {
        ok: false,
        error: busError('E_NOT_FOUND', `實例不存在：${args.uid}`, {
          hint: '執行中的實例請讀 $sys/registry/instances。',
        }),
      }
    }

    const templateId =
      args.template ?? (this.nodes.get(`${SYS_ROOT}/layout/template`)?.value as string)
    if (templateId === 'free') {
      return {
        ok: false,
        error: busError('E_UNSUPPORTED', '目前沒有選用板型，無處可吸附', {
          hint: '先把 $sys/layout/template 設成某個板型，或在呼叫時指定 template。',
          details: { available: this.layouts.map((t) => t.id) },
        }),
      }
    }

    const template = this.layouts.find((candidate) => candidate.id === templateId)
    if (!template || template.cells.length === 0) {
      return {
        ok: false,
        error: busError('E_NOT_FOUND', `沒有這個板型：${templateId}`, {
          hint: '可用的板型請讀 $sys/layout/templates。',
          details: { available: this.layouts.map((t) => t.id) },
        }),
      }
    }

    const viewport = this.nodes.get(`${SYS_ROOT}/viewport`)?.value as Viewport | undefined
    if (!viewport || viewport.w <= 0 || viewport.h <= 0) {
      return {
        ok: false,
        error: busError('E_UNSUPPORTED', '宿主尚未回報桌面尺寸', {
          hint: '需由宿主呼叫 bus.setViewport() 提供。',
        }),
      }
    }

    const current = this.nodes.get(`${instance.prefix}/$window/rect`)?.value as
      | WindowRect
      | undefined
    const index =
      args.cell ?? (current ? nearestCellIndex(template.cells, viewport, current) : 0)
    const cell = template.cells[index]
    if (!cell) {
      return {
        ok: false,
        error: busError('E_NOT_FOUND', `板型 ${template.id} 沒有第 ${index} 格`, {
          hint: `這個板型有 ${template.cells.length} 格，索引從 0 開始。`,
        }),
      }
    }

    const rect = cellToRect(cell, viewport, template.gap ?? 0)
    const result = await this.write(`${instance.prefix}/$window/rect`, rect, {
      origin: `${SYS_ROOT}/cmd/snap`,
    })
    if (!result.ok) return { ok: false, error: result.error }
    return { ok: true, result: { rect, cell: index, template: template.id } }
  }

  private applyWriteOutcome(
    node: NodeRecord,
    incoming: unknown,
    outcome: WriteOutcome,
  ): WriteResult {
    if (!outcome.accept) {
      return {
        ok: false,
        error: busError('E_REJECTED', outcome.reason, { path: node.path }),
      }
    }

    const adopted = outcome.value !== undefined ? outcome.value : incoming

    // 擁有者可以修正值，但不能違反自己宣告的 schema
    const violations = validateValue(node.def.schema, adopted)
    if (violations.length > 0) {
      return {
        ok: false,
        error: busError('E_INTERNAL', `${node.path} 的擁有者採用了不符合自身 schema 的值`, {
          path: node.path,
          details: violations,
        }),
      }
    }

    if (node.def.kind === 'state') {
      const encoded = encodeValue(adopted, node.path)
      if (!encoded.ok) return { ok: false, error: encoded.error }
      this.setValue(node, encoded.clone)
      return { ok: true, value: encoded.clone }
    }

    // command：無保留值
    return { ok: true, value: adopted }
  }

  // ───────────────────────────────── 內部：逾時與中止

  private async guard<T>(
    work: () => Promise<{ ok: true; value: T } | { ok: false; error: BusError }>,
    timeoutMs: number,
    path: string,
    instance: InstanceRecord | null,
    kind: 'write' | 'call',
  ): Promise<{ ok: true; value: T } | { ok: false; error: BusError }> {
    let timer: ReturnType<typeof setTimeout> | undefined
    let abort: ((error: BusError) => void) | undefined

    const control = new Promise<{ ok: false; error: BusError }>((resolve) => {
      timer = setTimeout(() => {
        resolve({
          ok: false,
          error: busError('E_TIMEOUT', `${kind} 逾時（${timeoutMs} ms）：${path}`, {
            path,
            hint: '逾時不代表操作沒有生效——擁有者可能只是回應慢。請重讀狀態確認。',
          }),
        })
      }, timeoutMs)
      abort = (error) => resolve({ ok: false, error })
    })

    if (instance && abort) instance.pending.add(abort)
    try {
      return await Promise.race([work(), control])
    } finally {
      if (timer !== undefined) clearTimeout(timer)
      if (instance && abort) instance.pending.delete(abort)
    }
  }

  // ───────────────────────────────── 內部：遞送（§7.5）

  private deliver(message: Message): void {
    const pathSegments = splitPath(message.path)
    for (const sub of this.subs.values()) {
      if (!sub.active) continue
      if (!matchSegments(sub.segments, pathSegments)) continue
      this.dispatch(sub, message)
    }
  }

  /** 一律非同步遞送，避免在 publish 的呼叫堆疊中重進入。microtask 佇列保證 FIFO。 */
  private dispatch(sub: Subscription, message: Message): void {
    queueMicrotask(() => {
      if (!sub.active) return
      try {
        sub.handler(message)
      } catch (cause) {
        if (this.onHandlerError) {
          this.onHandlerError(cause, sub.pattern)
        } else {
          queueMicrotask(() => {
            throw cause
          })
        }
      }
    })
  }
}

// ─────────────────────────────────────────────── 純函式輔助

/** 0–1 的格子比例 → 像素矩形 */
function cellToRect(cell: LayoutCell, viewport: Viewport, gap: number): WindowRect {
  const half = gap / 2
  return {
    x: Math.round(cell.x * viewport.w + half),
    y: Math.round(cell.y * viewport.h + half),
    w: Math.round(cell.w * viewport.w - gap),
    h: Math.round(cell.h * viewport.h - gap),
  }
}

/**
 * 中心距離最近的格子。拖曳放開時用它決定要吸到哪裡。
 *
 * 匯出是因為 shell 在拖曳過程中要即時高亮落點——兩邊必須用同一份判斷，
 * 否則「看到的框」跟「實際吸到的格」會不一致。
 */
export function nearestCellIndex(
  cells: readonly LayoutCell[],
  viewport: Viewport,
  rect: WindowRect,
): number {
  const cx = rect.x + rect.w / 2
  const cy = rect.y + rect.h / 2
  let best = 0
  let bestDistance = Infinity
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i] as LayoutCell
    const dx = (cell.x + cell.w / 2) * viewport.w - cx
    const dy = (cell.y + cell.h / 2) * viewport.h - cy
    const distance = dx * dx + dy * dy
    if (distance < bestDistance) {
      bestDistance = distance
      best = i
    }
  }
  return best
}

/** 一欄版面左右各留這麼多。與 fitToViewport 的 margin 同一個數，因為它們是同一件事的兩半：那一邊是「別讓它出去」，這一邊是「就住在這裡」。 */
const COLUMN_MARGIN = 8

/** 比這個窄的固定螢幕排成一欄。往上是平板與看板，那裡放得下兩欄，一欄反而浪費——兩個 240 寬的元件並排，再窄就擠在一起了。 */
const COLUMN_MAX_W = 520

/** 活動事件裡的值要小而可預期——它每一次寫入都會發一份 */
const ACTIVITY_VALUE_BYTES = 1_024

function compactActivityValue(value: unknown): { value: unknown; truncated: boolean } {
  let json: string | undefined
  try {
    json = JSON.stringify(value)
  } catch {
    return { value: '[無法序列化]', truncated: true }
  }
  if (json === undefined) return { value: null, truncated: false }
  if (json.length <= ACTIVITY_VALUE_BYTES) return { value: JSON.parse(json) as unknown, truncated: false }
  return { value: `${json.slice(0, 200)}…（已截斷，共 ${json.length} 字元）`, truncated: true }
}

function clamp(requested: number | undefined, fallback: number, max: number): number {
  if (typeof requested !== 'number' || !Number.isFinite(requested) || requested <= 0) {
    return fallback
  }
  return Math.min(requested, max)
}

function noOwner(path: string): BusError {
  return busError('E_NO_OWNER', `${path} 的擁有者已消失`, {
    path,
    hint: '該 app 已關閉或當機。請重讀 $sys/registry/instances 確認目前有哪些實例。',
  })
}

function schemaError(
  path: string,
  def: NodeDef,
  violations: { at: string; message: string }[],
): BusError {
  const first = violations[0]
  return busError('E_SCHEMA', `值不符合 ${path} 的 schema：${first?.message ?? ''}`, {
    path,
    hint: `此節點期望：${JSON.stringify(def.schema)}${def.unit ? `，單位 ${def.unit}` : ''}。${def.description}`,
    details: violations,
  })
}

function checkKindAccess(kind: NodeKind, access: Access, path: string): BusError | null {
  const legal: Record<NodeKind, Access[]> = {
    state: ['r', 'rw'],
    command: ['w'],
    event: ['r'],
    action: ['w'],
  }
  if (!legal[kind].includes(access)) {
    return busError('E_SCHEMA', `kind "${kind}" 不能搭配 access "${access}"`, {
      path,
      hint: `kind ${kind} 的合法 access 為：${legal[kind].join(' / ')}。`,
    })
  }
  return null
}

function checkWritable(kind: NodeKind, access: Access, path: string): BusError | null {
  if (kind === 'action') {
    return busError('E_UNSUPPORTED', `${path} 是 action，不能用 write()`, {
      path,
      hint: '請改用 call()，它會回傳執行結果。',
    })
  }
  if (kind === 'event') {
    return busError('E_NOT_WRITABLE', `${path} 是 event，只能訂閱`, { path })
  }
  if (kind === 'state' && access === 'r') {
    return busError('E_NOT_WRITABLE', `${path} 是唯讀狀態`, {
      path,
      hint: '量測值與系統狀態一律唯讀。可調整的設定通常在 config/ 底下。',
    })
  }
  return null
}

/**
 * 值一不一樣。用 JSON 比而不是 ===，因為出廠值常常是物件（例如一個 rect），
 * 而每次註冊都是新的字面值，`===` 永遠不成立。
 */
function same(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b)
}
